import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { ReadableNavButton } from "../components/Sticker";
import { useAuth } from "../lib/auth";
import { backLabel, defaultBackLabel, defaultBackPath, goBack } from "../lib/navBack";
import { FieldHint } from "../components/createEvent/CreateEventFormUi";
import HostProfileView from "../components/profile/HostProfileView";
import { upcomingEventsOnly } from "../lib/publicListingFormat";

const SKELETON = "rounded-md2 bg-black/[0.06]";

function HostProfileLoadingSkeleton() {
  return (
    <div className="min-h-screen bg-cream max-w-page mx-auto px-4 pt-5 pb-16" aria-busy="true" aria-label="Loading host profile">
      <div className="animate-pulse motion-reduce:animate-none space-y-6">
        <div className={`h-11 w-28 ${SKELETON}`} />
        <div className={`h-52 rounded-card bg-night/20`} />
        <div className={`h-40 rounded-card bg-night/15`} />
        <div className={`h-52 rounded-card ${SKELETON}`} />
      </div>
      <p className="sr-only">Loading host profile…</p>
    </div>
  );
}

export default function HostProfilePage() {
  const { organizerId } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const { account } = useAuth();
  const [host, setHost] = useState(null);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const backText = backLabel(location, defaultBackLabel(account));

  useEffect(() => {
    if (!organizerId) {
      setError("not_found");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/organizers/${organizerId}/public`);
        setHost(r.data.organizer);
        setSchools(r.data.schools || []);
        setClasses(r.data.classes || []);
        setEvents(upcomingEventsOnly(r.data.events || []));
      } catch {
        setError("not_found");
      } finally {
        setLoading(false);
      }
    })();
  }, [organizerId]);

  useEffect(() => {
    if (!host?.name) return undefined;
    const previousTitle = document.title;
    document.title = `${host.name} — rsvp.dance`;
    return () => {
      document.title = previousTitle;
    };
  }, [host?.name]);

  if (loading) {
    return <HostProfileLoadingSkeleton />;
  }

  if (error || !host) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream p-8 text-center">
        <p className="font-ui font-semibold text-[18px] text-ink">Host not found.</p>
        <FieldHint className="mt-2 max-w-sm">
          This link may be outdated, or the host hasn&apos;t set up a public profile yet.
        </FieldHint>
        <ReadableNavButton
          as={Link}
          to="/events"
          className="mt-5 px-5"
          data-testid="host-profile-not-found-events"
        >
          Browse events
        </ReadableNavButton>
      </div>
    );
  }

  return (
    <HostProfileView
      host={host}
      schools={schools}
      classes={classes}
      events={events}
      className="min-h-screen pb-16"
      leading={
        <ReadableNavButton
          type="button"
          onClick={() => goBack(nav, location, [defaultBackPath(account)])}
          className="px-4"
          data-testid="host-profile-back-btn"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} aria-hidden />
          {backText}
        </ReadableNavButton>
      }
    />
  );
}
